import { ChangeEvent, useState } from "react";
import { Form } from "react-bootstrap";
import TextField from "@components/FormsV2/TextField/TextField";
import GenericToggle from "@components/FormsV2/GenericToggle/GenericToggle";
import UserDefinedFieldCollection from "@components/UserDefinedFieldCollection/UserDefinedFieldCollection";
import "./style.css";
import type { ResourceFormValues } from "../types";

export interface ResourceFormProps {
  id: string;
  initialValues: ResourceFormValues;
  isSubmitting: boolean;
  onSubmit: (values: ResourceFormValues) => void;
}

export const ResourceForm = ({
  id,
  initialValues,
  isSubmitting,
  onSubmit,
}: ResourceFormProps): JSX.Element => {
  const [values, setValues] = useState<ResourceFormValues>(initialValues);

  const set = <K extends keyof ResourceFormValues>(
    key: K,
    v: ResourceFormValues[K]
  ) => setValues((prev) => ({ ...prev, [key]: v }));

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isSubmitting) return;
    onSubmit(values);
  };

  return (
    <Form
      id={id}
      data-testid={id}
      className="resourceForm"
      onSubmit={handleSubmit}
    >
      <div className="row">
        <div className="col-md-4">
          <Form.Label htmlFor="resource-name">Name</Form.Label>
          <TextField
            label={null}
            id="resource-name"
            value={values.name}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              set("name", e.target.value)
            }
            required
          />
          <small className="text-muted">
            Name of resource used for internal discussions
          </small>
        </div>
        <div className="col-md-4">
          <Form.Label htmlFor="resource-external-name">
            External Name
          </Form.Label>
          <TextField
            label={null}
            id="resource-external-name"
            value={values.externalName}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              set("externalName", e.target.value)
            }
          />
          <small className="text-muted">
            Name of resource shown to customers
          </small>
        </div>
        <div className="col-md-4">
          <Form.Label>Default</Form.Label>
          <div className="resourceDefaultToggle">
            <GenericToggle
              key={`default-${initialValues.default}`}
              initialValueOfIsOn={values.default}
              onToggle={(v: boolean) => set("default", v)}
              onText="Default"
              offText="Not default"
              hasText
            />
          </div>
          <small className="small text-muted d-block">
            When set, imported services with no matching resource are assigned
            to this resource.
          </small>
        </div>
      </div>

      <div className="row">
        <div className="col-md-12">
          <Form.Label htmlFor="resource-description">Description</Form.Label>
          <TextField
            label={null}
            id="resource-description"
            value={values.description}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              set("description", e.target.value)
            }
          />
        </div>
      </div>

      {values.userDefinedFields.length > 0 && (
        <>
          <hr />
          <UserDefinedFieldCollection
            userDefinedFields={values.userDefinedFields}
            sectionTitle="User Defined Fields"
            disabled={false}
            onChange={(changed) =>
              setValues((prev) => ({
                ...prev,
                userDefinedFields: prev.userDefinedFields.map((f) =>
                  f.name === changed.name ? changed : f
                ),
              }))
            }
          />
        </>
      )}
    </Form>
  );
};

export default ResourceForm;
